import React, { useMemo, useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { ChevronDown, ChevronUp } from "lucide-react";
import UserAvatar from "../common/UserAvatar";

const fontBold = "Segoe UI Semibold, sans-serif, system-ui";
const fontNormal = "Segoe UI Regular, sans-serif, system-ui";

const formatHours = (secs) => {
  if (!secs) return "0h";
  const hours = secs / 3600;
  return hours >= 10 ? `${Math.round(hours)}h` : `${hours.toFixed(1)}h`;
};

function relativeTime(dateStr) {
  if (!dateStr) return null;
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return null;
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function isOlderThan24h(dateStr) {
  if (!dateStr) return false;
  return Date.now() - new Date(dateStr).getTime() > 86400000;
}

const MetricCell = ({ value, total, color, tooltipLabel }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <Tooltip title={total > 0 ? `${pct}% ${tooltipLabel || ""}` : ""} arrow>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "2px", justifyContent: "center" }}>
        <Typography component="span" sx={{ fontSize: 12, fontFamily: fontBold, color }}>
          {value}
        </Typography>
        {total > 0 && (
          <Typography component="span" sx={{ fontSize: 10, fontFamily: fontNormal, color: "hsl(150, 10%, 46%)" }}>
            /{total}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

const COLUMNS = [
  { key: "internal_user", label: "Internal user", flex: 2, sortable: false },
  { key: "candidates_created", label: "Candidates", flex: 1 },
  { key: "applications_submitted", label: "Applications", flex: 1 },
  { key: "reached", label: "Reached", flex: 1 },
  { key: "missed", label: "Missed", flex: 1 },
  { key: "interested", label: "Sign ups", flex: 1 },
  { key: "callback", label: "Callback", flex: 1 },
  { key: "applied", label: "Applied", flex: 1 },
  { key: "sign_rate", label: "Sign rate", flex: 1 },
  { key: "applied_rate", label: "App rate", flex: 1 },
  { key: "external_profiles_sourced", label: "Profiles", flex: 1 },
  { key: "hours_logged_secs", label: "Hours", flex: 1 },
  { key: "last_activity_at", label: "Last activity", flex: 1.2, sortable: false },
];

const CoordinatorUsersTable = ({ rows, onUserClick }) => {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("desc");

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      return sortDir === "desc" ? bv - av : av - bv;
    });
  }, [rows, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  return (
    <Box sx={{ backgroundColor: "#ffffff", border: "1px solid hsl(140, 8%, 88%)", borderRadius: "8px", overflow: "hidden" }}>
      <Box sx={{ display: "flex", px: "16px", py: "10px", borderBottom: "1px solid hsl(140, 8%, 88%)", alignItems: "center" }}>
        {COLUMNS.map((col) => (
          <Box
            key={col.key}
            component={col.sortable === false ? "div" : "button"}
            onClick={col.sortable === false ? undefined : () => toggleSort(col.key)}
            sx={{
              all: col.sortable === false ? undefined : "unset",
              flex: col.flex,
              display: "flex",
              alignItems: "center",
              gap: "2px",
              minWidth: 0,
              justifyContent: col.key === "internal_user" ? "flex-start" : "center",
              cursor: col.sortable === false ? "default" : "pointer",
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                fontFamily: fontBold,
                color: "hsl(150, 10%, 46%)",
              }}
              noWrap
            >
              {col.label}
            </Typography>
            {col.sortable !== false && sortKey === col.key &&
              (sortDir === "desc" ? <ChevronDown size={11} /> : <ChevronUp size={11} />)}
          </Box>
        ))}
      </Box>

      {sortedRows.map((row) => {
        const inactive = row.reached_total === 0 && !row.candidates_created && !row.applications_submitted;
        const signRate = row.sign_rate ?? 0;
        const appRate = row.applied_rate ?? 0;
        const lastAct = relativeTime(row.last_activity_at);

        let lastActColor = "hsl(150, 10%, 46%)";
        if (!row.last_activity_at) lastActColor = "hsl(0, 72%, 51%)";
        else if (isOlderThan24h(row.last_activity_at)) lastActColor = "hsl(38, 92%, 50%)";

        return (
          <Box
            key={row.internal_user.id}
            onClick={() => onUserClick?.(row.internal_user.id)}
            sx={{
              display: "flex",
              alignItems: "center",
              px: "16px",
              py: "14px",
              borderBottom: "1px solid hsl(140, 8%, 88%)",
              cursor: "pointer",
              transition: "background-color 150ms ease",
              borderLeft: inactive ? "3px solid hsl(0, 72%, 51%)" : "3px solid transparent",
              "&:hover": { backgroundColor: "hsl(140, 8%, 90%)" },
            }}
          >
            <Box sx={{ flex: 2, display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
              <UserAvatar user={row.internal_user} size={28} />
              <Typography sx={{ fontSize: 12, fontFamily: fontBold, color: "hsl(150, 30%, 12%)" }} noWrap>
                {row.internal_user.full_name}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, textAlign: "center" }}>
              <Typography sx={{ fontSize: 12, fontFamily: fontBold, color: "hsl(174, 60%, 38%)" }}>
                {row.candidates_created}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, textAlign: "center" }}>
              <Typography sx={{ fontSize: 12, fontFamily: fontBold, color: "hsl(262, 65%, 55%)" }}>
                {row.applications_submitted}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <MetricCell value={row.reached} total={row.reached_total} color="hsl(142, 71%, 45%)" tooltipLabel="reached" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <MetricCell value={row.missed} total={row.reached_total} color="hsl(0, 72%, 51%)" tooltipLabel="missed" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <MetricCell value={row.interested} total={row.reached} color="hsl(38, 92%, 50%)" tooltipLabel="sign ups" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <MetricCell value={row.callback} total={row.reached} color="hsl(217, 91%, 55%)" tooltipLabel="callback" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <MetricCell value={row.applied} total={row.reached} color="hsl(150, 38%, 17%)" tooltipLabel="applied" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, textAlign: "center" }}>
              <Typography sx={{ fontSize: 12, fontFamily: fontBold, color: signRate > 0 ? "hsl(142, 71%, 45%)" : "hsl(150, 10%, 46%)" }}>
                {signRate}%
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, textAlign: "center" }}>
              <Typography sx={{ fontSize: 12, fontFamily: fontBold, color: appRate > 0 ? "hsl(142, 71%, 45%)" : "hsl(150, 10%, 46%)" }}>
                {appRate}%
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, textAlign: "center" }}>
              <Typography sx={{ fontSize: 12, fontFamily: fontBold, color: "hsl(150, 30%, 12%)" }}>
                {row.external_profiles_sourced}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, textAlign: "center" }}>
              <Typography sx={{ fontSize: 12, fontFamily: fontNormal, color: "hsl(150, 10%, 46%)" }}>
                {formatHours(row.hours_logged_secs)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1.2, minWidth: 0, textAlign: "center" }}>
              <Typography sx={{ fontSize: 11, fontFamily: fontNormal, color: lastActColor }} noWrap>
                {lastAct || "Not started"}
              </Typography>
            </Box>
          </Box>
        );
      })}

      {sortedRows.length === 0 && (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography sx={{ fontSize: 12.5, fontFamily: fontNormal, color: "hsl(150, 10%, 46%)" }}>
            No internal users found.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CoordinatorUsersTable;
